// @flow
import * as React from 'react';
import { ButtonBase, Divider, Stack, Typography } from '@mui/material';
import moment from 'moment/moment';
import _ from 'lodash';
import { memo } from 'react';
import { StatusDto } from '@coverright/data-access/types/enrollment';
import { BlinkingWarningIcon, useStatusForm, withStatusForm } from '../../../../features';
import { useClient } from '../../hooks/client-context';
import { Tooltip } from '@coverright/ui/inputs';

interface Props {
  status?: StatusDto,
  disqualifiedReason?: string | null,
  followUpDate?: string,
  expectedCloseDate?: string,
}

export const StatusDates = memo((props: Props) => {
  const {open} = useStatusForm();
  const client= useClient();

  if (!props.status || ![StatusDto.FollowUp, StatusDto.Contacted, StatusDto.SalesQualified, StatusDto.Disqualified].includes(props.status)) {
    return null
  }

  const dates: React.ReactNode[] = [];

  if (props.status === StatusDto.Disqualified) {
    dates.push(<Stack gap={{ xl: 1 }} direction={{ xl: 'row' }} alignItems={{ xl: 'center' }}>
      <Typography variant={'body2'} fontWeight={500} noWrap>Disqualified reason:</Typography>
      <Typography component={'p'} variant={'caption'} color={'error.600'}>
        {props.disqualifiedReason ? _.upperFirst(_.lowerCase(props.disqualifiedReason)) : 'unknown'}
      </Typography>
    </Stack>)
  } else {
    dates.push(
      <Stack gap={{ xl: 1 }} direction={{ xl: 'row' }} alignItems={{ xl: 'center' }}>
        <Typography variant={'body2'} fontWeight={500} noWrap>Next contact date:</Typography>
        <FollowUpDate {...props} />
      </Stack>
    )

    if (props.status === StatusDto.SalesQualified) {
      dates.push(
        <Stack gap={{ xl: 1 }} direction={{ xl: 'row' }} alignItems={{ xl: 'center' }}>
          <Typography variant={'body2'} fontWeight={500} noWrap>Expected close date:</Typography>
          <ExpectedCloseDate {...props} />
        </Stack>
      )
    }
  }

  return (
    <ButtonBase onClick={() => open(client)} sx={{textAlign: 'left'}}>
      <Stack gap={1} direction={'row'} alignItems={'center'}>
        {dates.map((item, i) => <React.Fragment key={i}>
          {item}
          {i < dates.length - 1 && <Divider orientation={'vertical'} sx={{mx: 1, alignSelf: 'stretch'}} />}
        </React.Fragment>)}
      </Stack>
    </ButtonBase>
  );
});

export const ClientStatusDates = withStatusForm(StatusDates)

const FollowUpDate = ({followUpDate, status}: Props) => {
  const shouldHaveDate = followUpDate && status && [StatusDto.FollowUp, StatusDto.Contacted, StatusDto.SalesQualified, StatusDto.ApplicationSubmitted].includes(status)
  const date = shouldHaveDate ? moment(followUpDate).format('L') : 'n/a';
  const isExpired = moment(followUpDate).isBefore(moment(), 'day');
  const color = shouldHaveDate ? isExpired ? 'error.500' : 'success.600' : '#b9b9b9'
  return <Tooltip title={isExpired ? 'Next contact date is expired.' : ''}>
    <Stack direction={'row'} alignItems={'center'} gap={.5}>
      {isExpired && <BlinkingWarningIcon size={20} />}
      <Typography variant={'body2'} color={color} fontWeight={isExpired ? 500 : 400} noWrap>
        {date}
      </Typography>
    </Stack>
  </Tooltip>
}

const ExpectedCloseDate = ({expectedCloseDate, status}: Props) => {
  const shouldHaveDate = expectedCloseDate && StatusDto.SalesQualified === status
  return <Typography variant={'body2'} color={shouldHaveDate ? 'success.600' : '#b9b9b9'}>
    {shouldHaveDate ? moment(expectedCloseDate).format('L') : 'n/a'}
  </Typography>
}
